import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { KEY_RECOIL, TYPE_BEAT_ENUM, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import {
  DEFAULT_CONTROL_MODAL_VALUE,
  selectActiveTab,
  selectControlModal,
  selectEventChanges,
  selectEventNews,
  selectGroupEventChanges,
  selectHolterEvents,
  selectIsLoadingStrip,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
  BEAT_HR_TAB_ID,
} from '@/entities/report';
import {
  updateMultipleStripReview,
  updateMultipleStripType,
  updateStripReview,
  updateStripType,
} from '@/entities/beat';
import {
  checkEventDataChange,
  getEventEffectdByUpdatedEvent,
  getMultipleEventEffectdByUpdatedEvent,
} from '@/entities/event';

/**
 * Drives the afib event type toolbar on the beats tab, exposing each action's enable state plus the handlers that retype, review and reset the selected ECG events. Resolves the selected strip by preferring a pending change over the loaded holter event, merges it with the multi-selection, derives the disable and reviewed flags with memoised passes over pending changes and new events, and registers a window keydown listener mapping the A, R, D and C keys onto those handlers.
 *
 * @returns The toolbar state and handlers for the afib event types, including the tab key, loading and disable flags, the reviewed and reset indicators, and the retype, review, reset, page-index and control-modal setters.
 */
const useAfibButtonTypes = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Replaces the pending ECG event changes for the beats tab. Forwards the value to the store's setEventChanges action with this tab's key prepended, so the per-tab record is the one written.
   *
   * @param v - The next pending-changes list, or an updater receiving the previous one.
   * @returns The result of the store's event-changes action.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the event list on the beats tab to another page. Forwards the value to the store's setPageIndex action with this tab's key prepended, so the per-tab record is the one written.
   *
   * @param v - The next page index, or an updater receiving the previous one.
   * @returns The result of the store's page-index action.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  /**
   * Reads the list of newly created ECG events for the beats tab without subscribing to it. Applies the event-news selector bound to this tab's key against the report store snapshot taken through getState, so reset logic works from the freshest list.
   *
   * @returns The current array of newly added events for the tab.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Replaces the list of newly created ECG events for the beats tab. Forwards the value to the store's setEventNews action with this tab's key prepended, so the per-tab record is the one written.
   *
   * @param v - The next new-events list, or an updater receiving the previous one.
   * @returns The result of the store's event-news action.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Updates the modal-control flags for the beats tab. Forwards the value to the store's setControlModal action with this tab's key prepended, so the per-tab record is the one written.
   *
   * @param v - The next control-modal value, or an updater receiving the previous one.
   * @returns The result of the store's control-modal action.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads the current modal-control flags for the beats tab outside of React's render subscription. Pulls the live report store state via getState and applies the control-modal selector bound to this tab's key, so keyboard handlers see the latest value rather than a captured one.
   *
   * @returns The control-modal flag record for the tab, carrying isShowChangeDurationButtonType among others.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Updates the event options that back the undo and redo history for the beats tab. Forwards the value to the store's setEventOptions action with this tab's key prepended, so a whole retype, review or reset pass is recorded as one undoable group.
   *
   * @param v - The next event-options value, or an updater receiving the previous one.
   * @returns The result of the store's event-options action.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark; this file
  // reads/writes event-only fields (idEvent, isNew, originalData, ...) that IBeat/IEcgBookmark lack - pre-existing gap.
  const sletedEvent = useReportStore(selectSelectedStrip(keyRecoil)) as any;
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Sets which ECG strip is focused on the beats tab. Forwards the value to the store's setSelectedStrip action with this tab's key prepended, so the per-tab record is the one written.
   *
   * @param v - The next selected strip, or an updater receiving the previous one.
   * @returns The result of the store's selected-strip action.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const eventNews = useReportStore(selectEventNews(keyRecoil));

  const selectedStrip: any = useMemo(() => {
    const foundEventChange = eventChanges.find((x) => x.idEvent === sletedEvent?.idEvent);
    if (foundEventChange) {
      return foundEventChange;
    }
    const foundEvent = holterEvents.find((x) => x.idEvent === sletedEvent?.idEvent);
    if (foundEvent) {
      return foundEvent;
    }
    return { ...(sletedEvent || {}), ...(sletedEvent?.originalData || {}) };
  }, [sletedEvent, eventChanges, holterEvents]);
  const mergeSelectedStrip = _.filter(
    _.uniqBy([selectedStrip || {}, ...(selectedMultipleStrip || [])], 'idEvent'),
    (x) => !_.isEmpty(x),
  );

  const foundEventChange: any = useMemo(() => {
    const { event } = getEventEffectdByUpdatedEvent(selectedStrip, eventNews, eventChanges);
    return event;
  }, [eventChanges, selectedStrip, eventNews]);
  const isSelectNewEvent = selectedStrip?.isNew;
  const isNotSelectedStrip = _.isEmpty(selectedMultipleStrip) && !selectedStrip?.idEvent;
  const isEmptyEventList = _.isEmpty(selectedMultipleStrip) && isNotSelectedStrip;
  const isSelectedMultipleStrip = !_.isEmpty(mergeSelectedStrip) && mergeSelectedStrip.length > 1;
  const isGroupEventChanges = !!groupEventChanges.find((x) => x.groupEventType === activeButton);
  // foundEventChange.type (TYPE_BEAT_EVENT_ENUM | TYPE_RHYTHM_EVENT_ENUM) never overlaps TYPE_BEAT_ENUM - pre-existing.
  const isChangeToDeletedEvent = (foundEventChange as any)?.type === TYPE_BEAT_ENUM.DELETED;
  /**
   * Marks whether the afib type buttons must be locked because a save is in flight for the affected ECG events. Is truthy when the pending change for the focused strip is saving, or when any strip in the multi-selection has a matching pending change flagged as saving.
   *
   * @returns A truthy value while any selected event is mid-save, otherwise falsy.
   */
  const isDisableWhenSaving =
    foundEventChange?.isSaving ||
    selectedMultipleStrip.find((x) => eventChanges.find((d) => d.idEvent === x.idEvent)?.isSaving);
  const isDisableButton =
    isEmptyEventList ||
    isGroupEventChanges ||
    isSelectNewEvent ||
    isDisableWhenSaving ||
    isLoadingStrip;
  const isDisableChangeDuration =
    isNotSelectedStrip ||
    isChangeToDeletedEvent ||
    isGroupEventChanges ||
    isSelectNewEvent ||
    isDisableWhenSaving ||
    isSelectedMultipleStrip ||
    isLoadingStrip;
  const isDisableChangingButton = isDisableButton || controlModal.isShowChangeDurationButtonType;

  const isShowResetButton = useMemo(
    () =>
      !!foundEventChange ||
      selectedMultipleStrip.find((event: any) => {
        const { event: deleteEvent } = getEventEffectdByUpdatedEvent(
          event,
          eventNews,
          eventChanges,
        );
        if (deleteEvent) {
          return true;
        }
        return !!eventChanges.find((x) => x.idEvent === event?.idEvent);
      }),
    [foundEventChange?.idEvent, selectedMultipleStrip, eventChanges, eventNews],
  );

  const isEventReviewed = useMemo(() => {
    if (selectedMultipleStrip.length > 0) {
      let reviewedCount = 0;
      _.forEach(mergeSelectedStrip, (x) => {
        const ele = eventChanges.find((d) => d.idEvent === x.idEvent);
        if (ele) {
          if (ele.isReviewed) {
            reviewedCount += 1;
          }
        } else if (x.isReviewed) {
          reviewedCount += 1;
        }
      });
      if (reviewedCount === mergeSelectedStrip.length) return true;
      return false;
    }
    if (selectedStrip?.id) {
      return foundEventChange ? foundEventChange.isReviewed : selectedStrip.isReviewed;
    }
    return false;
  }, [selectedMultipleStrip, selectedStrip, eventChanges, mergeSelectedStrip]);

  /**
   * Applies a new ECG event type to whatever is currently selected on the beats tab. Recomputes the pending change list with updateMultipleStripType for a multi-selection or updateStripType for a single strip, records the resulting list into the event options so the action can be undone as one group, then commits it as the tab's event changes.
   *
   * @param type - The beat or rhythm event type to assign to the selected strips.
   * @returns Nothing.
   */
  const handleChangeStripType = (type: any) => {
    if (selectedMultipleStrip.length > 0) {
      const stripChangesUpdate = updateMultipleStripType({
        stripChanges: eventChanges,
        strips: mergeSelectedStrip,
        type,
      });
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: stripChangesUpdate,
      }));
      setEventChanges(stripChangesUpdate);
      return;
    }
    if (selectedStrip?.idEvent) {
      const stripChangesUpdate = updateStripType({
        stripChanges: eventChanges,
        strip: selectedStrip,
        type,
      });
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: stripChangesUpdate,
      }));
      setEventChanges(stripChangesUpdate);
    }
  };

  /**
   * Toggles the reviewed flag on the selected ECG events for the beats tab. Builds the updated pending changes for the multi-selection or the single strip, drops any entry that checkEventDataChange finds identical to the original holter event so a pure review toggle back to its starting value leaves no pending change, stores the list into the event options for grouped undo, then commits it as the tab's event changes.
   *
   * @param isReviewed - The reviewed state to apply to the selected events.
   * @returns Nothing.
   */
  const onClickMarkReview = (isReviewed: boolean) => {
    if (selectedMultipleStrip.length > 0) {
      const stripChangesUpdate = updateMultipleStripReview({
        stripChanges: eventChanges,
        strips: mergeSelectedStrip,
        isReviewed,
      });
      const listIdRemove: any[] = [];
      _.forEach(mergeSelectedStrip, (selectedStrip) => {
        const changedEvent = _.find(stripChangesUpdate, (x) => x.idEvent === selectedStrip.idEvent);
        const originalEvent = _.find(holterEvents, (x) => x.idEvent === selectedStrip.idEvent);
        if (!checkEventDataChange(originalEvent, changedEvent)) {
          listIdRemove.push(selectedStrip.idEvent);
        }
      });
      _.remove(stripChangesUpdate, (x) => listIdRemove.includes(x.idEvent));
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: stripChangesUpdate,
      }));
      setEventChanges(stripChangesUpdate);
      return;
    }
    if (selectedStrip?.idEvent) {
      const stripChangesUpdate = updateStripReview({
        stripChanges: eventChanges,
        strip: selectedStrip,
        isReviewed,
      });
      const changedEvent = _.find(stripChangesUpdate, (x) => x.idEvent === selectedStrip.idEvent);
      const originalEvent = _.find(holterEvents, (x) => x.idEvent === selectedStrip.idEvent);
      //* Remove from event change if user only change review to original
      if (checkEventDataChange(originalEvent, changedEvent)) {
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventChangesState: stripChangesUpdate,
        }));
        setEventChanges(stripChangesUpdate);
      } else {
        _.remove(stripChangesUpdate, (x) => x.idEvent === selectedStrip.idEvent);
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventChangesState: stripChangesUpdate,
        }));
        setEventChanges(stripChangesUpdate);
      }
    }
  };

  /**
   * Discards the pending edits on the selected ECG events and restores their loaded holter values. Partitions the pending changes into kept and reset entries, walks the overlap links from getMultipleEventEffectdByUpdatedEvent and each reset event's own linkEvents to drop the new events and sibling changes those edits created, writes back the trimmed new-event list only when it shrank, reselects the original holter event, and records both lists for grouped undo.
   *
   * @returns Nothing.
   */
  const onClickReset = () => {
    const resetEvents: any[] = [];
    const clonedEventNews = _.cloneDeep(getEventNews());

    //* Remove những event đang được select khỏi mảng event changes
    const newEventChanges = eventChanges.filter((x) => {
      if (x.idEvent !== selectedStrip?.idEvent) {
        const foundEventInMultipleSelected = selectedMultipleStrip.find(
          (strip) => strip.idEvent === x.idEvent,
        );
        if (foundEventInMultipleSelected) {
          resetEvents.push(x);
          return false;
        }
        return true;
      }
      resetEvents.push(x);
      return false;
    });

    const linkEvents = getMultipleEventEffectdByUpdatedEvent(
      mergeSelectedStrip,
      eventNews,
      eventChanges,
    );
    // linkEventNew/linkEventChange (handler.ts, Task 7) are typed IEvent | {}, so .id/.idEvent
    // don't exist on the {} branch - pre-existing.
    _.forEach(linkEvents, (linkEvent: any) => {
      _.remove(clonedEventNews, (x) => {
        if (x.id === linkEvent?.linkEventNew?.id) {
          return true;
        }
        return false;
      });
      _.remove(newEventChanges, (x) => {
        if (x.idEvent === linkEvent?.linkEventChange?.idEvent) {
          return true;
        }
        return false;
      });
    });

    //* Check những event bị remove
    //* New event chắc chắn có link event nếu overlap còn event change thì tùy event bị change duration mới có link event nếu overlap
    resetEvents.forEach((resetEvent) => {
      //* Remove những event new / change liên quan tới event bị remove
      //* Trong new event sẽ check những event bị reset nếu liên quan tới new event thì new event đó bị remove
      _.remove(clonedEventNews, (eventNew) => {
        const { linkEvents } = eventNew;
        if (
          linkEvents?.length &&
          linkEvents.find((linkEvent) => linkEvent.idEvent === resetEvent.idEvent)
        ) {
          _.remove(newEventChanges, (x) =>
            linkEvents.find((linkEvent) => linkEvent.idEvent === x.idEvent),
          );
          return true;
        }
        return false;
      });
      //* Trong event change sẽ check nếu có event liên quan tới event bị reset thì remove event change đó
      if (resetEvent.linkEvents?.length) {
        _.remove(newEventChanges, (x) =>
          resetEvent.linkEvents.find((linkEvent: any) => linkEvent.idEvent === x.idEvent),
        );
      } else {
        eventChanges.forEach((eventChange) => {
          const { linkEvents } = eventChange;
          if (linkEvents?.length && linkEvents.find((x) => x.idEvent === resetEvent.idEvent)) {
            _.remove(
              newEventChanges,
              (x) =>
                linkEvents.find((linkEvent) => linkEvent.idEvent === x.idEvent) ||
                x.idEvent === eventChange.idEvent,
            );
          }
        });
      }
    });
    if (clonedEventNews.length !== getEventNews().length) {
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventNewsState: clonedEventNews,
      }));
      setEventNews(clonedEventNews);
    }

    setSelectedStrip(holterEvents.find((x) => x.idEvent === selectedStrip?.idEvent) || null);
    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventChangesState: newEventChanges,
    }));
    setEventChanges(newEventChanges);
  };

  useEffect(() => {
    /**
     * Maps keyboard shortcuts onto the afib event type actions while the beats tab is hovered. Ignores auto-repeat, strip loading, pointers outside the tab zone element and any chord holding ctrl or meta, then routes A to an artifact retype, R to toggling the reviewed flag, D to a delete retype and C to opening the change-duration modal, each behind its own disable flag.
     *
     * @param event - The keydown event carrying keyCode, repeat and the ctrl and meta modifier flags.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      switch (keyCode) {
        case 65: {
          // A
          // Check ctrl and meta to distinguish from ctrl+a to select all
          if (!isDisableChangingButton) {
            handleChangeStripType(TYPE_RHYTHM_EVENT_ENUM.ARTIFACT);
          }
          break;
        }
        case 82: {
          // R
          if (!isDisableChangingButton) {
            onClickMarkReview(!isEventReviewed);
          }
          break;
        }
        case 68: {
          // Delete (D)
          if (!isDisableChangingButton) {
            handleChangeStripType(TYPE_BEAT_ENUM.DELETED);
          }
          break;
        }
        case 67: {
          // Change duration C
          if (!isDisableChangeDuration && !getControlModal().isShowChangeDurationButtonType) {
            setControlModal({
              ...DEFAULT_CONTROL_MODAL_VALUE,
              isShowChangeDurationButtonType: true,
            });
          }
          break;
        }
        default:
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedMultipleStrip,
    selectedStrip,
    eventChanges,
    isDisableChangingButton,
    isEventReviewed,
    isLoadingStrip,
    activeButton,
    groupEventChanges,
  ]);

  return {
    keyRecoil,
    isLoadingStrip,
    activeButton,
    isGroupEventChanges,
    setPageIndex,
    isDisableChangingButton,
    isDisableChangeDuration,
    setControlModal,
    getControlModal,
    handleChangeStripType,
    onClickMarkReview,
    onClickReset,
    isEventReviewed,
    isShowResetButton,
  };
};

export default useAfibButtonTypes;
